import { stripToneMarks, toneFromMarks } from "@kensio/pinyinjs";

/**
 * What a reading files under, for a list arranged by how its words sound.
 *
 * 安定 comes back as `an1ding4` and 昂 as `ang2`, and comparing those two puts
 * every 安 word ahead of every 昂 word. That is where 现代汉语词典 puts them.
 * A key built from the whole reading at once cannot do it: `anding` against
 * `ang` reads `d` against `g` and files 安定 among the 昂 words. The tone digit
 * after each syllable is what closes the syllable off, and a digit weighs less
 * than any letter.
 *
 * The digits also put a run of homophones in tone order, dā, dá, dǎ, dà, rather
 * than in the order the tone marks happen to fall in. A neutral syllable is
 * written 5 by `CidianReadings.pinyinHtml` and files after the four, again
 * where 现代汉语词典 puts it.
 *
 * ü is written `v`, the way an input method spells it, so that lü is a syllable
 * of its own and files after every lu. The letters otherwise come from
 * `stripToneMarks`, since a tone mark is what stops `ǎ` filing beside `a`.
 *
 * **The syllables are read back out of the reading's own markup.**
 * `CidianReadings.pinyinHtml` writes one `<span class="py-syllable py-tone-N">`
 * per syllable, and this is the same expression `LijuIndexLine` reads them with
 * on the other side of the site. Neither the reading nor the characters say
 * where one syllable ends. The markup is the only thing that does.
 * `partials/pinyin-syllables.html` is the same arithmetic in a template, and
 * `partials/cidian-ordered.html` orders the site's listings from it.
 *
 * **A reading with no markup is read as one syllable**, its letters with the
 * spaces and apostrophes closed up and its tone marks counted off the end. That
 * is where the tones sat before the markup was read, and it still orders a run
 * of homophones. What it cannot do is tell 安定 from 昂, there being nothing to
 * say where the first syllable ends. Nothing in the section arrives that way.
 */
const syllables =
  /<span class="py-syllable(?: py-tone-(\d))?"[^>]*>([^<]*)<\/span>/gu;

/** One syllable's letters, as a key holds them. */
function letters(said: string): string {
  return stripToneMarks(said)
    .toLowerCase()
    .replaceAll("ü", "v")
    .replaceAll(/[\s']+/gu, "");
}

/** The tone of every marked vowel in a reading, in the order they are written. */
function marked(said: string): string {
  return [
    // oxlint-disable-next-line typescript/no-misused-spread -- code points are the unit: a tone is a diacritic on one letter, and no reading in the section holds a character outside the basic plane
    ...said,
  ]
    .map((letter) => toneFromMarks(letter))
    .filter((tone) => tone !== undefined)
    .join("");
}

/** How one reading is written and, where the entry has it, cut into syllables. */
export interface ReadingKeySource {
  /** The reading as a reader says it, tone marks and all. */
  readonly pinyin: string;

  /** The same reading as one span per syllable, where the entry carries it. */
  readonly pinyinHtml: string | undefined;
}

/**
 * One reading cut into syllables, each written as its letters and its tone.
 *
 * `an1ding4` comes back as `an1` and `ding4`. The two keys below are what a
 * caller wants, and this is the cut both of them are made from.
 */
export function readingSyllables(reading: ReadingKeySource): readonly string[] {
  const cut = [...(reading.pinyinHtml ?? "").matchAll(syllables)];

  if (cut.length === 0) {
    return [`${letters(reading.pinyin)}${marked(reading.pinyin)}`];
  }

  return cut.map(([, tone, said]) => `${letters(said ?? "")}${tone ?? ""}`);
}

/**
 * One reading cut into the syllables a reader sees, tone marks and all.
 *
 * `bìngfēi` comes back as `bìng` and `fēi`, where {@link readingSyllables}
 * above gives `bing4` and `fei1`. A 复习 card asking for one character of a
 * word draws the whole reading and marks the syllable that character is read
 * as, which wants the text rather than the key. See
 * `src/user/character-syllables.ts`.
 *
 * A reading with no markup comes back whole, there being nothing to say where
 * one syllable ends, which is the same answer the cut above gives.
 * @param reading - The reading as text and, where the entry has it, as markup.
 * @returns The syllables as they are written.
 */
export function saidSyllables(reading: ReadingKeySource): readonly string[] {
  const cut = [...(reading.pinyinHtml ?? "").matchAll(syllables)];

  return cut.length === 0
    ? [reading.pinyin]
    : cut.map(([, , said]) => said ?? "");
}

/**
 * Whether every syllable of a key carries a tone.
 *
 * `guo4` and `gu4shi4` do, and `guo5`, `gu4shi5` and a bare `guo` do not. A
 * neutral tone is what the key writes as 5, and a reading that arrived with no
 * markup and no tone mark writes nothing. Both read as toneless here.
 * @param key - A reading key, as {@link readingSortKey} writes one.
 * @returns Whether it holds no neutral syllable.
 */
export function fullyToned(key: string): boolean {
  return /^(?:[a-z]+[1-4])+$/u.test(key);
}

/** The key one reading files under. */
export function readingSortKey(reading: ReadingKeySource): string {
  return readingSyllables(reading).join("");
}

/**
 * The same reading with its tones taken off, syllable boundaries kept.
 *
 * `shi` for 是 and `xi-an` for 西安. What the second tier of a homophone list
 * is grouped by: the same syllables said in other tones, 十 and 是 under shi.
 *
 * **The hyphen is load-bearing.** The tone digits are what close a syllable
 * off in {@link readingSortKey}, so taking them away runs 西安 into 先 and puts
 * xī'ān among the words read xiān. `slug` in `@kensio/pinyinjs` keeps the two
 * apart the same way, and `docs/cidian.md` records the 13 readings that also
 * need ü held apart from u.
 */
export function tonelessReadingKey(reading: ReadingKeySource): string {
  return readingSyllables(reading)
    .map((syllable) => syllable.replaceAll(/\d/gu, ""))
    .join("-");
}

/**
 * {@link tonelessReadingKey} for a reading already cut into syllables.
 *
 * `["xī", "ān"]` is `xi-an`. A link to another entry carries its readings this
 * way, and a homophone block compares them with its own key.
 * @param said - Each syllable as it is written, tone marks and all.
 * @returns The key, its syllables hyphenated.
 */
export function tonelessSyllablesKey(said: readonly string[]): string {
  return said.map((syllable) => letters(syllable)).join("-");
}
